import type { UserRole } from '../types';

export interface DirectoryUser {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  role: UserRole;
  roles: UserRole[];
  created_at: string;
  last_sign_in_at: string | null;
  terms_accepted_at: string | null;
  deleted_at: string | null;
  trips: number;
  open_trips: number;
  paid_cents: number;
  tags: string[];
  two_fa: boolean;
  total_count: number;
}

export type DirectorySort = 'name' | 'email' | 'joined' | 'last_seen' | 'trips' | 'paid';

/** "today", "3 days ago", "2 months ago", or "never" when the person hasn't signed in. */
export function lastSeenLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'Never';
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function csvCell(v: string | number | null): string {
  const s = v == null ? '' : String(v);
  // A cell that starts with = + - @ would run as a formula in a spreadsheet.
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function usersToCsv(rows: DirectoryUser[]): string {
  const head = ['Name', 'Email', 'Roles', 'Status', 'Tags', '2FA', 'Last sign-in', 'Trips', 'Paid (EUR)', 'Joined', 'User ID'];
  const lines = rows.map((u) => [
    u.full_name,
    u.email,
    u.roles.join(' + '),
    u.deleted_at ? 'Deleted' : u.terms_accepted_at ? 'Active' : 'Terms not accepted',
    (u.tags ?? []).join(' | '),
    u.two_fa ? 'on' : 'off',
    u.last_sign_in_at ? u.last_sign_in_at.slice(0, 10) : '',
    u.trips,
    (u.paid_cents / 100).toFixed(2),
    u.created_at.slice(0, 10),
    u.id,
  ].map(csvCell).join(','));
  return [head.join(','), ...lines].join('\n');
}

export interface PanelAlert {
  tone: 'warn' | 'bad';
  text: string;
}

/** What an admin should notice first about a person, from rows the panel already loads. */
export function userAlerts(input: {
  profile: { deleted_at?: unknown; terms_accepted_at?: unknown } | null;
  hostProfile: { consecutive_missed_trips?: unknown; suspended_for_missed_trips_at?: unknown; host_agreement_accepted_at?: unknown; active?: unknown } | null;
  stripeConnect: { onboarding_complete?: unknown; payouts_enabled?: unknown } | null;
}): PanelAlert[] {
  const out: PanelAlert[] = [];
  const { profile, hostProfile, stripeConnect } = input;
  if (profile?.deleted_at) out.push({ tone: 'bad', text: 'This account was deleted.' });
  else if (!profile?.terms_accepted_at) out.push({ tone: 'warn', text: "Hasn't accepted the terms yet." });
  if (hostProfile) {
    if (hostProfile.suspended_for_missed_trips_at) out.push({ tone: 'bad', text: 'Suspended from matching after missed trips.' });
    else if (Number(hostProfile.consecutive_missed_trips) > 0) {
      out.push({ tone: 'warn', text: `${Number(hostProfile.consecutive_missed_trips)} missed trip${Number(hostProfile.consecutive_missed_trips) === 1 ? '' : 's'} in a row.` });
    }
    if (hostProfile.active === false) out.push({ tone: 'warn', text: 'Host profile is switched off.' });
    if (!hostProfile.host_agreement_accepted_at) out.push({ tone: 'warn', text: "Hasn't accepted the host agreement." });
    if (!stripeConnect || !stripeConnect.onboarding_complete) out.push({ tone: 'warn', text: 'Payout setup is not finished.' });
    else if (!stripeConnect.payouts_enabled) out.push({ tone: 'bad', text: 'Payouts are disabled in Stripe.' });
  }
  return out;
}

export interface AuditEntry {
  id: string;
  action: string;
  details: Record<string, unknown> | null;
  created_at: string;
  admin_id: string;
  admin_name: string | null;
}

const ROLE_ORDER: UserRole[] = ['traveler', 'host', 'admin'];

/** "traveler + host" in a fixed order, whatever order the roles were stored in. */
export function rolesLabel(roles: string[]): string {
  const known = ROLE_ORDER.filter((r) => roles.includes(r));
  return known.length ? known.join(' + ') : '—';
}

/** One line for the Activity tab. */
export function describeAudit(entry: Pick<AuditEntry, 'action' | 'details'>): string {
  const d = entry.details ?? {};
  switch (entry.action) {
    case 'roles_changed': {
      const before = Array.isArray(d.before) ? (d.before as string[]) : [];
      const after = Array.isArray(d.after) ? (d.after as string[]) : [];
      return `Changed roles: ${rolesLabel(before)} → ${rolesLabel(after)}`;
    }
    case 'host_cities_changed': {
      const n = (v: unknown) => (Array.isArray(v) ? v.length : 0);
      return `Changed the cities this host covers (${n(d.before)} → ${n(d.after)})`;
    }
    case 'two_fa_reset':
      return 'Reset two-factor authentication';
    case 'note_added':
      return 'Added a private note';
    case 'note_deleted':
      return 'Deleted a private note';
    case 'tags_changed': {
      const list = (v: unknown) => (Array.isArray(v) ? (v as string[]) : []);
      const before = list(d.before);
      const after = list(d.after);
      const added = after.filter((t) => !before.includes(t));
      const removed = before.filter((t) => !after.includes(t));
      return ['Changed tags', added.length ? `added ${added.join(', ')}` : '', removed.length ? `removed ${removed.join(', ')}` : '']
        .filter(Boolean).join(': ').replace(': added', ' — added').replace(': removed', ' — removed');
    }
    case 'password_reset_sent':
      return 'Sent a password-reset email';
    case 'confirmation_resent':
      return 'Sent the sign-up confirmation email again';
    default:
      return entry.action.replace(/_/g, ' ');
  }
}

/** What turning a role on or off means, said before the admin confirms. */
export function roleConsequences(before: string[], after: string[]): string[] {
  const out: string[] = [];
  const gained = (r: string) => after.includes(r) && !before.includes(r);
  const lost = (r: string) => before.includes(r) && !after.includes(r);
  if (gained('admin')) out.push('Gets full admin access: every user, trip, payment and setting, and can change roles.');
  if (lost('admin')) out.push('Loses admin access immediately.');
  if (gained('host')) out.push('Can be matched with travelers once their host profile is complete.');
  if (lost('host')) out.push('Is no longer offered to travelers. Trips already booked with them stay as they are.');
  return out;
}

export const MAX_TAGS_PER_PERSON = 10;
const TAG_PATTERN = /^[a-z0-9][a-z0-9 _-]{0,29}$/;

/** "VIP, press ,vip" -> ["vip", "press"]: lower case, trimmed, no repeats, only tags the server accepts. */
export function parseTags(input: string): { tags: string[]; invalid: string[] } {
  const seen = new Set<string>();
  const tags: string[] = [];
  const invalid: string[] = [];
  for (const raw of input.split(',')) {
    const t = raw.trim().toLowerCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    (TAG_PATTERN.test(t) ? tags : invalid).push(t);
  }
  return { tags, invalid };
}

export interface TwoFaSummaryRow {
  group_name: 'all' | 'admin' | 'host' | 'traveler';
  total: number;
  with_two_fa: number;
}

/** "12 of 41 accounts have 2FA · Admins 2/3 · Hosts 1/5 · Travelers 9/33" */
export function twoFaSummaryLine(rows: TwoFaSummaryRow[]): string {
  const by = new Map(rows.map((r) => [r.group_name, r]));
  const all = by.get('all');
  if (!all) return '';
  const part = (key: 'admin' | 'host' | 'traveler', label: string) => {
    const r = by.get(key);
    return r ? ` · ${label} ${r.with_two_fa}/${r.total}` : '';
  };
  return `${all.with_two_fa} of ${all.total} accounts have 2FA${part('admin', 'Admins')}${part('host', 'Hosts')}${part('traveler', 'Travelers')}`;
}
